// Module-level Audio singleton so a playing tale survives tab switches.
// Components subscribe via the `useSkazkiAudio` hook below.

import {useEffect, useSyncExternalStore} from 'react';

const listeners = new Set();

const state = {
    tale: null,       // currently loaded tale object or null
    isPlaying: false,
    currentSec: 0,
    durationSec: 0,
    error: null,
};

let audio = null;

function emit() {
    listeners.forEach(l => l());
}

function ensureAudio() {
    if (audio) return audio;
    audio = new Audio();
    audio.preload = 'metadata';
    audio.addEventListener('timeupdate', () => {
        state.currentSec = audio.currentTime || 0;
        emit();
    });
    audio.addEventListener('loadedmetadata', () => {
        state.durationSec = audio.duration || state.tale?.duration_sec || 0;
        emit();
    });
    audio.addEventListener('play', () => { state.isPlaying = true; emit(); });
    audio.addEventListener('pause', () => { state.isPlaying = false; emit(); });
    audio.addEventListener('ended', () => {
        state.isPlaying = false;
        state.currentSec = 0;
        emit();
    });
    audio.addEventListener('error', () => {
        state.error = 'playback';
        state.isPlaying = false;
        emit();
    });
    return audio;
}

function loadTale(tale) {
    const a = ensureAudio();
    const src = tale?.audio_url || tale?.preview_url || null;
    if (state.tale?.id === tale?.id && a.src && a.src === src) return;
    state.tale = tale;
    state.currentSec = 0;
    state.durationSec = tale?.duration_sec || 0;
    state.error = null;
    if (src) {
        a.src = src;
        if (typeof window !== 'undefined' && 'mediaSession' in navigator) {
            navigator.mediaSession.metadata = new window.MediaMetadata({
                title: tale.title,
                artist: `Для ${tale.child_name}, ${tale.age} лет`,
                artwork: tale.cover_url ? [{src: tale.cover_url, sizes: '512x512', type: 'image/svg+xml'}] : [],
            });
        }
    } else {
        a.removeAttribute('src');
        a.load();
    }
    emit();
}

function mountAudioElement(container) {
    const a = ensureAudio();
    a.controls = true;
    a.className = 'w-full';
    a.preload = 'metadata';
    a.controlsList = 'nodownload';
    container.appendChild(a);
    return () => {
        a.controls = false;
        a.className = '';
        if (a.parentNode === container) container.removeChild(a);
    };
}

export const skazkiAudio = {
    loadTale,
    mountAudioElement,
    play() {
        const a = ensureAudio();
        const promise = a.play();
        if (promise && typeof promise.catch === 'function') {
            promise.catch(() => { state.error = 'autoplay_blocked'; emit(); });
        }
    },
    pause() { ensureAudio().pause(); },
    toggle() {
        const a = ensureAudio();
        if (a.paused) this.play(); else this.pause();
    },
    seekBy(delta) {
        const a = ensureAudio();
        const next = Math.max(0, Math.min((a.currentTime || 0) + delta, a.duration || state.durationSec));
        a.currentTime = next;
    },
    seekTo(sec) {
        const a = ensureAudio();
        a.currentTime = Math.max(0, Math.min(sec, a.duration || state.durationSec));
    },
    stopAndClear() {
        if (audio) {
            audio.pause();
            audio.removeAttribute('src');
            audio.load();
        }
        state.tale = null;
        state.isPlaying = false;
        state.currentSec = 0;
        state.durationSec = 0;
        emit();
    },
    subscribe(l) { listeners.add(l); return () => listeners.delete(l); },
    getState() { return state; },
};

export function useSkazkiAudio() {
    return useSyncExternalStore(skazkiAudio.subscribe, skazkiAudio.getState, skazkiAudio.getState);
}

export function useReducedMotion() {
    const get = () => typeof window !== 'undefined'
        && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const subscribe = (cb) => {
        if (typeof window === 'undefined' || !window.matchMedia) return () => {};
        const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
        mq.addEventListener?.('change', cb);
        return () => mq.removeEventListener?.('change', cb);
    };
    return useSyncExternalStore(subscribe, get, () => false);
}

export function useQuietMode() {
    // Evening mode kicks in after 21:00 local time (PRD §07 "Тихий режим").
    const get = () => {
        if (typeof Date === 'undefined') return false;
        const h = new Date().getHours();
        return h >= 21 || h < 6;
    };
    // Re-evaluate every 5 minutes so evening mode engages mid-session.
    useEffect(() => {}, []);
    return useSyncExternalStore(
        (cb) => {
            const id = setInterval(cb, 5 * 60 * 1000);
            return () => clearInterval(id);
        },
        get,
        () => false,
    );
}
